// Shared risk-grade styling (pill badge + KPI cards). Keyed by the numeric
// grade value used in the results ('0'..'4'). Refined, quiet tints — colour
// is reserved for grade (the one thing that carries meaning), everything else
// stays neutral.
//
// The KPI accent (`color`) uses the system grade palette (`legendsColors`) so
// Results KPIs match the Risk Setup grade legend and the rest of the app.
import { legendsColors } from '@Components/RiskGrade/utils.RiskMatrix';

export interface GradeStyle {
	label: string;
	/** Vivid brand colour — used for the KPI card top accent bar. */
	color: string;
	/** Readable text colour for the grade chip (a darker shade of `color`).
	 *  Hand-picked per grade, not derived from `color` — deriving it
	 *  mathematically risks a shade that fails contrast on the chip's `bg`. */
	chipColor: string;
	/** Light tint for the chip background. Hand-picked alongside `chipColor`
	 *  for the same reason — the pair is chosen together for contrast. */
	bg: string;
	value: string;
}

// Consumers (StatusCell, KPI cards, matrix) are wired in later redesign
// slices — FE-10 (KPI cards) and FE-11 (Results grid). This file is the
// single source of truth; existing screens keep their current styling until
// those slices mount it behind the feature flag.

export const GRADE_CONFIG: Record<string, GradeStyle> = {
	'0': {
		// Chip text/tints follow the SAME hue as the KPI accent (`color` =
		// legendsColors), so the table grade badges match the KPI cards:
		// Optimal teal, Stable green, Moderate amber, Concerning orange, Critical red.
		value: '0',
		label: 'Optimal',
		color: legendsColors.negligible,
		chipColor: '#0e6d8c',
		bg: '#e3f2f8',
	},
	'1': {
		value: '1',
		label: 'Stable',
		color: legendsColors.low,
		chipColor: '#42750a',
		bg: '#edf5e1',
	},
	'2': {
		value: '2',
		label: 'Moderate',
		color: legendsColors.medium,
		chipColor: '#8a6400',
		bg: '#fdf3d4',
	},
	'3': {
		value: '3',
		label: 'Concerning',
		color: legendsColors.high,
		chipColor: '#b45309',
		bg: '#fdeadb',
	},
	'4': {
		value: '4',
		label: 'Critical',
		color: legendsColors.extreme,
		chipColor: '#8a181a',
		bg: '#f6e0e0',
	},
};

export const GRADE_ORDER = ['4', '3', '2', '1', '0'];
